import React, { useEffect, useState } from "react";
import AppShell from "../../components/AppShell";
import { ADMIN_NAV } from "./_nav";
import { api, errFrom } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../../components/ui/alert-dialog";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { Plus, Mail, UserCheck, UserMinus, Send, FileDown } from "lucide-react";

export default function AdminEmployees() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);

  async function load() {
    try { const { data } = await api.get("/admin/employees"); setList(data); }
    catch (e) { toast.error(errFrom(e)); }
  }
  useEffect(() => { load(); }, []);

  async function addEmployee() {
    if (!email || !name) return toast.error("Email and name required");
    setLoading(true);
    try {
      await api.post("/admin/employees", { email, name, phone });
      toast.success("Invite email sent");
      setOpen(false); setEmail(""); setName(""); setPhone("");
      load();
    } catch (e) { toast.error(errFrom(e)); }
    finally { setLoading(false); }
  }

  async function setStatus(id, status) {
    try {
      await api.post(`/admin/employees/${id}/status`, { status });
      toast.success(status === "active" ? "Reinstated" : "Terminated");
      load();
    } catch (e) { toast.error(errFrom(e)); }
  }

  async function resendInvite(id) {
    try {
      await api.post(`/admin/employees/${id}/resend-invite`);
      toast.success("Invite resent");
    } catch (e) { toast.error(errFrom(e)); }
  }

  async function downloadReceipt(e) {
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/employees/${e.id}/policy-receipt`;
      const r = await fetch(url, { credentials: "include" });
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.detail || "Receipt not available"); }
      const blob = await r.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href; a.download = `policy-receipt-${(e.name || "employee").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(href);
    } catch (err) { toast.error(err.message || "Download failed"); }
  }

  return (
    <AppShell nav={ADMIN_NAV} title="Employees" subtitle="Manage team">
      <div className="flex justify-end mb-5">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#FF3B30] hover:bg-[#E02920] h-11 px-5" data-testid="add-employee-button"><Plus size={16} className="mr-2" /> Add employee</Button>
          </DialogTrigger>
          <DialogContent className="bg-[#141414] border-white/10 text-white" data-testid="add-employee-dialog">
            <DialogHeader><DialogTitle className="font-heading">Invite a new employee</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div><Label className="text-overline">Full name</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="mt-2 bg-[#0A0A0A] border-white/10 h-11" data-testid="add-employee-name" /></div>
              <div><Label className="text-overline">Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 bg-[#0A0A0A] border-white/10 h-11" data-testid="add-employee-email" /></div>
              <div><Label className="text-overline">Phone (optional)</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-2 bg-[#0A0A0A] border-white/10 h-11" data-testid="add-employee-phone" /></div>
            </div>
            <DialogFooter>
              <Button onClick={addEmployee} disabled={loading} className="bg-[#FF3B30] hover:bg-[#E02920]" data-testid="add-employee-submit"><Mail size={14} className="mr-2" />{loading ? "Sending…" : "Send invite"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="brand-surface rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-[#0A0A0A]">
            <tr className="text-overline text-left">
              <th className="py-3 px-4">Name</th>
              <th className="py-3 px-4">Email</th>
              <th className="py-3 px-4">Phone</th>
              <th className="py-3 px-4">Status</th>
              <th className="py-3 px-4">Stage</th>
              <th className="py-3 px-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && (<tr><td colSpan="6" className="py-10 text-center text-zinc-500">No employees yet. Invite your first teammate.</td></tr>)}
            {list.map((e) => (
              <tr key={e.id} className="border-t border-white/5" data-testid={`employee-row-${e.id}`}>
                <td className="py-4 px-4 font-semibold">{e.name}</td>
                <td className="py-4 px-4 text-zinc-400">{e.email}</td>
                <td className="py-4 px-4 text-zinc-500">{e.phone || "—"}</td>
                <td className="py-4 px-4"><StatusBadge status={e.status} /></td>
                <td className="py-4 px-4 text-zinc-400 text-xs uppercase tracking-wider">{e.onboarding_stage || "—"}</td>
                <td className="py-4 px-4 text-right">
                  <div className="flex justify-end gap-2">
                    {e.status === "invited" && (
                      <Button size="sm" variant="ghost" onClick={() => resendInvite(e.id)} className="text-zinc-300 hover:text-white" data-testid={`resend-invite-${e.id}`}><Send size={14} className="mr-1" />Resend</Button>
                    )}
                    {(e.status === "active" || e.status === "onboarding" || e.status === "terminated") && (
                      <Button size="sm" variant="ghost" onClick={() => downloadReceipt(e)} className="text-zinc-300 hover:text-white hidden md:inline-flex" title="Download signed policy receipt" data-testid={`download-receipt-${e.id}`}><FileDown size={14} className="mr-1" />Receipt</Button>
                    )}
                    {e.status === "terminated" ? (
                      <Button size="sm" onClick={() => setStatus(e.id, "active")} className="bg-green-600 hover:bg-green-500 h-8" data-testid={`reinstate-${e.id}`}><UserCheck size={14} className="mr-1" />Reinstate</Button>
                    ) : (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8" data-testid={`terminate-${e.id}`}><UserMinus size={14} className="mr-1" />Terminate</Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-[#141414] border-white/10 text-white">
                          <AlertDialogHeader>
                            <AlertDialogTitle className="font-heading">Terminate {e.name}?</AlertDialogTitle>
                            <AlertDialogDescription className="text-zinc-400">They'll lose access immediately. You can reinstate later.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="bg-[#1C1C1E] border-white/10 text-white hover:bg-[#2C2C2E]">Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => setStatus(e.id, "terminated")} className="bg-red-600 hover:bg-red-500" data-testid={`confirm-terminate-${e.id}`}>Terminate</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </AppShell>
  );
}

function StatusBadge({ status }) {
  const map = {
    active: ["bg-green-500/15 text-green-400 border-green-500/30", "Active"],
    onboarding: ["bg-amber-500/15 text-amber-400 border-amber-500/30", "Onboarding"],
    invited: ["bg-blue-500/15 text-blue-400 border-blue-500/30", "Invited"],
    terminated: ["bg-red-500/15 text-red-400 border-red-500/30", "Terminated"],
  };
  const [cls, label] = map[status] || ["bg-zinc-500/15 text-zinc-400 border-zinc-500/30", status];
  return <Badge className={`${cls} border text-[10px] uppercase tracking-widest font-semibold`}>{label}</Badge>;
}
